import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState, type ChangeEvent } from "react";
import {
    Button,
    Card,
    CloseButton,
    Description,
    Input,
    Label,
    Link,
    ProgressBar,
    Spinner,
    Surface,
    TextArea,
    TextField,
} from "@heroui/react";
import { ChevronRight, Plus } from "lucide-react";
import { ToastUtil } from "@/utils/toast";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { buildCos, uploadFileToOss } from "@/utils/cos/cos";
import { UserAPI } from "@repo/api/user";
import { appConfig } from "@/config/app-config";
import { useAuth } from "@repo/context/auth/auth";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";
import { v4 as uuidv4 } from "uuid";

export const Route = createFileRoute("/help/feedback")({
    component: FeedbackPage,
});


export default function FeedbackPage() {
    useDocumentTitle("意见反馈 - 兽聚汇");

    const [description, setDescription] = useState("");
    const [contact, setContact] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});

    const navigate = useNavigate();
    const { userInfo } = useAuth();

    // 处理文件选择
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files);

            // 检查文件类型
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/avi', 'video/mov'];
            const validFiles = newFiles.filter(file => {
                if (!allowedTypes.includes(file.type)) {
                    ToastUtil.showError(`${file.name} 文件类型不支持，请选择图片或视频文件`);
                    return false;
                }

                // 检查文件大小限制（每个文件最大10MB）
                const maxSize = 100 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    ToastUtil.showError(`${file.name} 文件过大，请选择小于100MB的文件`);
                    return false;
                }

                return true;
            });

            // 检查文件数量限制（最多5个文件）
            if (selectedFiles.length + validFiles.length > 5) {
                ToastUtil.showError("最多只能上传5个文件");
                return;
            }

            setSelectedFiles(prev => [...prev, ...validFiles]);
        }
        // 清空input值，允许重复选择同一文件
        event.target.value = "";
    };

    // 删除文件
    const handleDeleteFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // 提交反馈：先统一上传到 temp/{uid}/，再把 key 交给接口，由后端落库
    const handleSubmit = async () => {
        if (!userInfo?.uid) {
            ToastUtil.showError("请先登录");
            return;
        }

        if (!description.trim()) {
            ToastUtil.showError("请填写问题描述");
            return;
        }

        if (description.length < 10) {
            ToastUtil.showError("问题描述至少需要10个字符");
            return;
        }

        setIsSubmitting(true);

        const initialProgress: { [key: string]: number } = {};
        selectedFiles.forEach(file => {
            initialProgress[file.name] = 0;
        });
        setUploadProgress(initialProgress);

        const uploadPromises = selectedFiles.map((file) => {
            const extMatch = file.name.match(/(\.[^./\\]+)$/);
            const ext = extMatch ? extMatch[1] : "";
            const fileKey = `temp/${userInfo.uid}/${Date.now()}_${uuidv4()}${ext}`;
            const cos = buildCos(fileKey);
            return uploadFileToOss({
                cos,
                fileKey,
                file,
                bucket: appConfig.cos.mainBucket,
                region: appConfig.cos.mainBucketRegion,
                onProgress: (progress) => {
                    setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: Math.round(progress.percent * 100)
                    }));
                }
            }).then(() => fileKey).catch((error) => {
                console.error(`${file.name} 上传失败:`, error);
                throw new Error(`${file.name} 上传失败: ${error.message}`);
            });
        });

        Promise.all(uploadPromises).then((uploadedFileKeys) => {
            return UserAPI.submitFeedback({
                description: description.trim(),
                contact: contact.trim() || undefined,
                attachments: uploadedFileKeys.filter(key => key),
            });
        }).then(() => {
            // 清空表单和进度状态
            setDescription("");
            setContact("");
            setSelectedFiles([]);
            setUploadProgress({});

            // 跳转到反馈成功页面
            navigate({ to: "/result/success", search: { type: "success", title: "反馈提交成功", end: "true", op: "返回帮助中心", redirect: "/help" }, replace: true });
        }).catch((error) => {
            console.error('提交过程出错:', error);
            const errorMsg = error instanceof Error ? error.message : "提交失败，请重试";
            ToastUtil.showError(errorMsg);
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const finishedCount = Object.values(uploadProgress).filter((progress) => progress === 100).length;
    const overallProgress = selectedFiles.length > 0
        ? Object.values(uploadProgress).reduce((sum, progress) => sum + progress, 0) / selectedFiles.length
        : 0;

    return (
        <SafeAreaContainer className="mx-auto flex min-h-screen flex-col gap-4 bg-background p-4">
            <Card className="w-full">
                <Card.Header>
                    <Card.Title>您的意见对我们十分重要！</Card.Title>
                    <Card.Description>
                        由于我们的开发人员有限，我们深知APP有许多改进空间，也许有考虑不周的地方。如果您有更好的建议或者意见，还请反馈给我们！
                    </Card.Description>
                </Card.Header>
                <Card.Content className="flex flex-col gap-2 text-sm text-muted">
                    <p>
                        此外，我们也设立了
                        <Link
                            className="mx-1"
                            href="https://docs.qq.com/sheet/DZG1raFlGdnVDdkJu"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            兽聚汇问题反馈收集表
                            <Link.Icon />
                        </Link>
                        方便大家跟进我们的工作进度。
                    </p>
                    <p>如果您的问题不在列表中，请通过下面的表单来填写您的反馈。</p>
                </Card.Content>
            </Card>

            <TextField
                name="description"
                fullWidth
                isRequired
                isDisabled={isSubmitting}
                value={description}
                onChange={setDescription}
            >
                <Label>问题描述</Label>
                <Description>必填，您的详细描述能帮助我们更快的定位问题</Description>
                <TextArea
                    placeholder="请详细描述您遇到的问题..."
                    rows={5}
                    maxLength={500}
                    variant="secondary"
                />
                <Description className={description.length >= 500 ? "text-danger" : undefined}>
                    {description.length}/500
                </Description>
            </TextField>

            <Surface variant="default" className="flex w-full flex-col gap-3 rounded-xl p-3">
                <div className="flex flex-col gap-1">
                    <Label>相关截图/录屏</Label>
                    <Description>选填，提供相关截图能帮助我们的开发组了解问题发生的过程</Description>
                </div>
                <div className="flex flex-wrap gap-2">
                    {selectedFiles.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="relative size-20 shrink-0">
                            <Surface
                                variant="secondary"
                                className="flex size-20 items-center justify-center overflow-hidden rounded-lg"
                            >
                                {file.type.startsWith("image/") ? (
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        className="size-20 object-cover"
                                    />
                                ) : (
                                    <div className="p-2 text-center text-xs text-muted">
                                        <div className="font-medium">{file.name.split(".").pop()?.toUpperCase()}</div>
                                        <div className="mt-1 text-[10px]">
                                            {(file.size / 1024 / 1024).toFixed(1)}MB
                                        </div>
                                    </div>
                                )}
                                {isSubmitting && uploadProgress[file.name] !== undefined && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs font-medium text-white">
                                        {uploadProgress[file.name]}%
                                    </div>
                                )}
                            </Surface>
                            <CloseButton
                                aria-label={`移除 ${file.name}`}
                                className="absolute -top-1 -right-1"
                                isDisabled={isSubmitting}
                                onPress={() => handleDeleteFile(index)}
                            />
                        </div>
                    ))}
                    {selectedFiles.length < 5 && (
                        <Button
                            aria-label="添加截图或录屏"
                            variant="secondary"
                            className="h-20 w-20"
                            isDisabled={isSubmitting}
                            onPress={() => fileInputRef.current?.click()}
                        >
                            <Plus className="size-4" />
                        </Button>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                />
                {isSubmitting && selectedFiles.length > 0 && (
                    <ProgressBar aria-label="上传进度" size="sm" value={overallProgress}>
                        <Label>
                            上传进度: {finishedCount} / {selectedFiles.length} 个文件完成
                        </Label>
                        <ProgressBar.Track>
                            <ProgressBar.Fill />
                        </ProgressBar.Track>
                    </ProgressBar>
                )}
            </Surface>

            <TextField
                name="contact"
                fullWidth
                isDisabled={isSubmitting}
                value={contact}
                onChange={setContact}
            >
                <Label>联系方式</Label>
                <Description>选填，如果您在资料中绑定了QQ，可无需填写</Description>
                <Input placeholder="您的联系方式（QQ、微信、邮箱等）" variant="secondary" />
            </TextField>

            <Button
                variant="secondary"
                fullWidth
                className="h-auto justify-between py-4"
                onPress={() =>
                    window.open(
                        "https://qun.qq.com/universal-share/share?ac=1&authKey=oRiH4pAbrCOktmtGodMGQGLFkTa9iZE5wHbFYY3zVwvAQcvGshON%2FeuOfE%2BZg%2B8x&busi_data=eyJncm91cENvZGUiOiI5MDgyNzQ5NTgiLCJ0b2tlbiI6Ik1jR1FKQ0o2WGd4ejg4T1h6Ym5wbEpsRE5tR0JsaitCTHdSUnVZWkJJSUdpVjhXNFRvTTdYMVVVTzFiZmVScG8iLCJ1aW4iOiIxMzMwNDQwMDExIn0%3D&data=UIA3C56Fd_qkcfXQcY2ZBLXoatme7slazMlpIkFmRMcxbXaTVtuti9JdstenzWVjI3kDJycypH0ha3itZqOT1A&svctype=4&tempid=h5_group_info",
                        "_blank",
                    )
                }
            >
                <span className="flex flex-col items-start text-left">
                    <span className="text-base font-medium">需要更快速的支持？</span>
                    <span className="text-sm font-normal text-muted">点此处添加官方客服QQ群（908274958）</span>
                </span>
                <ChevronRight className="size-6 shrink-0" />
            </Button>

            <Button
                fullWidth
                onPress={handleSubmit}
                isPending={isSubmitting}
                isDisabled={isSubmitting || !description.trim()}
            >
                {({ isPending }) => (
                    <>
                        {isPending && <Spinner size="sm" color="current" />}
                        {isPending
                            ? selectedFiles.length > 0
                                ? `上传中... ${finishedCount}/${selectedFiles.length}`
                                : "提交中..."
                            : "提交反馈"}
                    </>
                )}
            </Button>
        </SafeAreaContainer>
    );
}
